/* XV Capital Partners — Comprehensive FR/EN translations dictionary
 * ------------------------------------------------------------------
 * Centralised strings used across the entire public site. Keep keys
 * stable; add new keys at the same depth in both locales.
 */
export const translations = {
    fr: {
        nav: {
            home: "Accueil",
            about: "À propos",
            expertises: "Expertises",
            approach: "Approche",
            ecosystem: "Écosystème",
            collectivites: "Collectivités",
            articles: "Articles",
            contact: "Contact",
            private: "Espace privé",
            cta: "Premier entretien",
        },
        common: {
            discover: "Découvrir",
            learnMore: "En savoir plus",
            readArticle: "Lire l'article",
            requestMeeting: "Demander un entretien confidentiel",
            allArticles: "Tous les articles",
            backToArticles: "Tous les articles",
            language: "Langue",
            seeMore: "Voir plus",
            loading: "Chargement…",
            noArticles: "Aucun article publié pour le moment.",
            discoverApproach: "Découvrir l'approche",
            initiateConversation: "Initier la conversation",
            scroll: "Scroll",
            by: "Par",
            allRightsReserved: "Tous droits réservés",
            legal: "Mentions légales",
            privacy: "Confidentialité",
            terms: "CGU",
        },
        footer: {
            navigation: "Navigation",
            contact: "Nous joindre",
            description:
                "Cabinet indépendant de strategic advisory et d'architecture financière — à la croisée du développement d'entreprise et des réseaux internationaux d'experts.",
            tagline: "Strategic advisory · Architecture financière",
            requestInterview: "Demander un entretien",
            legal:
                "XV Capital Partners est un cabinet indépendant de strategic advisory et d'architecture financière. Le cabinet n'exerce aucune activité de gestion, de placement financier, de conseil en investissement réglementé, ni d'intermédiation financière. Il ne procède à aucune sollicitation publique d'épargne et n'exerce aucune activité de démarchage bancaire ou financier au sens des articles L. 341-1 et suivants du Code monétaire et financier.",
        },
        home: {
            heroBadge: "Strategic advisory · Côte d'Azur",
            cta1: "Premier entretien",
            cta2: "Notre approche",
            ctaExpertises: "Découvrir nos expertises",
            hero: {
                badge: "Strategic advisory · Côte d'Azur",
                title: "L'architecture financière",
                titleEmphasis: "des ambitions rares.",
                subtitle:
                    "XV Capital Partners accompagne les dirigeants et investisseurs dans la préparation et la structuration d'acquisitions, de financements et de partenariats stratégiques en France et à l'international. Nous coordonnons les expertises juridiques, fiscales et financières adaptées à chaque opération et mobilisons les partenaires pertinents.",
            },
            manifesto: {
                kicker: "Manifeste · Cabinet indépendant",
                line1: "Les opérations qui marquent",
                line2: "ne se traitent ni seul,",
                line3: "ni en série.",
                lead: "Une architecture juridique, fiscale et financière calibrée à la mesure d'une ambition.",
                p1: "XV Capital Partners coordonne, autour de chaque opération, un cercle restreint d'experts — droit des sociétés, fiscalité patrimoniale et corporate, audit, due diligence, gouvernance, relations investisseurs — sélectionnés pour la discrétion et la rigueur de leur exécution.",
                p2: "Une préparation rigoureuse, une coordination réactive et un calendrier adapté aux enjeux de chaque opération.",
            },
            stats: {
                kicker: "Sélectivité · Discrétion · Vision long terme",
                items: [
                    { value: 8, suffix: "", label: "Places financières internationales" },
                    { value: 4, suffix: "", label: "Continents couverts par le réseau" },
                    { value: 20, suffix: " ans+", label: "Expérience terrain" },
                ],
            },
            services: {
                kicker: "Métiers",
                title1: "Une intervention,",
                title2: "quatre dimensions.",
                intro:
                    "Chaque mission combine, à des degrés différents, ces quatre dimensions — coordonnées en interne et adossées à un réseau d'experts indépendants.",
                items: [
                    {
                        kicker: "I",
                        title: "Architecture financière & patrimoniale",
                        desc: "Conception, à la mesure de chaque dirigeant, d'une architecture financière et patrimoniale cohérente — coordonnée, discrète, pensée pour le long terme.",
                    },
                    {
                        kicker: "II",
                        title: "Opérations de croissance & transmission",
                        desc: "Accompagnement stratégique des opérations de développement, de croissance et de transmission — préservation de la gouvernance et de la vision du dirigeant.",
                    },
                    {
                        kicker: "III",
                        title: "Ingénierie de financement & structuration",
                        desc: "Solutions de financement adaptées aux enjeux patrimoniaux et entrepreneuriaux — coordination d'experts indépendants et de partenaires internationaux.",
                    },
                    {
                        kicker: "IV",
                        title: "Réseau international d'experts",
                        desc: "Coordination d'un écosystème international d'experts et de finance privée — mobilisé, mandat par mandat, dans une logique de cercle restreint et confidentiel.",
                    },
                ],
            },
            collectivitesSpot: {
                kicker: "Acteurs institutionnels & territoires",
                title1: "Accompagner les",
                title2: "territoires.",
                text: "Pour les acteurs publics et institutionnels, le cabinet coordonne un écosystème international d'experts et de partenaires financiers de long terme — dédié à la structuration d'opérations d'envergure au service du développement territorial.",
                tiles: [
                    { v: "Long terme", l: "Horizon" },
                    { v: "International", l: "Réseau" },
                    { v: "Sélectif", l: "Mandats" },
                    { v: "Confidentiel", l: "Mode opératoire" },
                ],
            },
            presence: {
                kicker: "Présence internationale",
                title1: "Un cabinet indépendant,",
                title2: "un réseau international.",
                text: "XV Capital Partners coordonne, selon les besoins de chaque mission, un réseau de partenaires et d'experts indépendants à l'international.",
            },
            insights: {
                kicker: "Articles",
                title1: "Notes d'analyses,",
                title2: "écrites pour les dirigeants.",
                seeAll: "Lire toutes les notes",
            },
            ctaFinal: {
                kicker: "Premier entretien · Confidentiel",
                title1: "Une opération exigeante ?",
                title2: "Parlons-en, à huis clos.",
                text: "Chaque mission s'ouvre par un entretien confidentiel d'évaluation. Le cabinet n'accepte qu'un nombre restreint de mandats chaque trimestre.",
            },
        },
        about: {
            kicker: "À propos",
            title: "Un cabinet fondé sur",
            titleEmphasis: "la finance privée",
            titleSuffix: "et l'accompagnement stratégique.",
            lead:
                "XV Capital Partners est un cabinet indépendant de strategic advisory, dédié aux opérations stratégiques et financières des dirigeants, investisseurs privés et family offices. Discret par choix, exigeant par méthode.",
            founderKicker: "Fondateur",
            founderRole: "Founder & Managing Partner",
            founderName: "Joel Kotho",
            founderBody: {
                p1_part1: "Formé aux métiers de la",
                p1_part2: "comptabilité et de la gestion",
                p1_part3: ", son parcours l'a conduit à évoluer dans les univers de la",
                p1_part4: "banque, de l'assurance, de l'industrie et de l'entrepreneuriat",
                p1_part5:
                    " — lui offrant une compréhension concrète des enjeux financiers, patrimoniaux et stratégiques auxquels sont confrontés les décideurs.",
                p2_part1: "Au fil des années, il a développé un",
                p2_part2: "réseau de confiance",
                p2_part3:
                    "réunissant dirigeants, investisseurs, family offices, partenaires stratégiques et acteurs institutionnels entre l'Europe, l'Afrique, le Moyen-Orient et l'Amérique du Nord. Cette capacité à connecter les bonnes expertises et les bons interlocuteurs autour de situations complexes constitue aujourd'hui l'un des fondements de l'approche XV Capital Partners.",
                p3_part1: "Parallèlement à ce parcours professionnel, il représente le",
                p3_part2: "Congo au niveau international en rugby",
                p3_part3:
                    ". Cette expérience du haut niveau lui transmet une culture durable de la discipline, de l'engagement, de la préparation et de la responsabilité collective, qui continue aujourd'hui d'inspirer son approche des projets complexes et des environnements exigeants.",
                p4_part1:
                    "Sa conviction est simple : les opérations les plus sensibles exigent davantage qu'une expertise technique. Elles nécessitent une capacité à",
                p4_part2: "coordonner des intérêts multiples",
                p4_part3:
                    ", à créer les bonnes conditions de confiance et à inscrire chaque décision dans une vision de long terme.",
                p5_part1:
                    "À travers XV Capital Partners, Joel Kotho accompagne des opérations de croissance, de transmission, de structuration patrimoniale et de développement international selon une même exigence :",
                p5_part2:
                    "engagement, discrétion, sélectivité et vision long terme",
                p5_part3: ".",
            },
            institutionsLabel: "Parcours institutionnel",
            pillarsKicker: "Une signature personnelle",
            pillarsTitle1: "Quatre principes,",
            pillarsTitle2: "une seule manière.",
            pillarsIntro:
                "Les codes appris dans le sport de haut niveau structurent aujourd'hui la manière dont le cabinet conduit chaque mandat.",
            valuesKicker: "Quatre principes",
            valuesTitle1: "Ce qui guide",
            valuesTitle2: "chaque mandat.",
            valuesIntro:
                "La confiance ne se demande pas. Elle se gagne, dossier par dossier, par un engagement sans compromis et une discrétion absolue.",
            values: [
                {
                    kicker: "I",
                    title: "Discrétion",
                    desc: "Les informations sont partagées avec les seuls intervenants nécessaires à la mission, dans un cadre de confidentialité convenu avec le client.",
                },
                {
                    kicker: "II",
                    title: "Rigueur",
                    desc: "Une méthodologie issue de la finance internationale et du conseil aux dirigeants. Anticiper, structurer, sécuriser.",
                },
                {
                    kicker: "III",
                    title: "Sélectivité",
                    desc: "Un nombre restreint de mandats par trimestre. L'attention totale, sans dilution, à chaque opération acceptée.",
                },
                {
                    kicker: "IV",
                    title: "Réseau",
                    desc: "Un cercle international de family offices, investisseurs privés et experts indépendants, bâti sur deux décennies.",
                },
            ],
            ecoKicker: "Un cercle d'experts",
            ecoTitle1: "Au-delà d'une équipe,",
            ecoTitle2: "un écosystème.",
            ecoText:
                "XV Capital Partners coordonne un cercle restreint d'experts indépendants — droit des sociétés, fiscalité, audit, due diligence, gouvernance, relations investisseurs — mobilisés sur-mesure pour chaque opération.",
            finalTitle1: "Une opération à structurer",
            finalTitle2: "avec exigence ?",
            finalCta: "Premier entretien confidentiel",
        },
        expertises: {
            kicker: "Métiers · Strategic advisory",
            title1: "Trois métiers,",
            title2: "une architecture sur-mesure.",
            lead:
                "XV Capital Partners intervient à la croisée du développement d'entreprise, de la structuration patrimoniale et des réseaux internationaux d'experts — chaque mandat étant calibré au plus juste du contexte du dirigeant ou de l'acteur institutionnel accompagné.",
            labelIntervention: "Intervention",
            horizontalKicker: "Expertises mobilisées",
            horizontalTitle1: "Dix expertises,",
            horizontalTitle2: "un seul interlocuteur.",
            horizontalIntro:
                "Chaque opération mobilise un sous-ensemble de ces expertises, coordonné en interne et adossé à un cercle d'avocats fiscalistes, de spécialistes du droit des affaires, d'experts-comptables et de professionnels du back-office.",
            horizontalItems: [
                { n: "I", title: "Stratégie & vision long terme" },
                { n: "II", title: "Architecture financière & patrimoniale" },
                { n: "III", title: "Ingénierie juridique" },
                { n: "IV", title: "Fiscalité patrimoniale & corporate" },
                { n: "V", title: "Expertise comptable & audit" },
                { n: "VI", title: "Due diligence & analyse" },
                { n: "VII", title: "Coordination d'experts indépendants" },
                { n: "VIII", title: "Structuration internationale" },
                { n: "IX", title: "Gouvernance & conformité" },
                { n: "X", title: "Advisory dirigeants & family offices" },
            ],
            collectivitesTeaseKicker: "Acteurs institutionnels & territoires",
            collectivitesTeaseTitle1: "Accompagner les territoires",
            collectivitesTeaseTitle2: "dans leurs grandes opérations.",
            collectivitesTeaseText:
                "Pour les collectivités, opérateurs publics et acteurs du développement économique, le cabinet coordonne un écosystème international d'experts et de partenaires financiers de long terme — calibré au caractère structurant de chaque projet.",
        },
        operations: {
            kicker: "Domaines d'Intervention",
            title: "Opérations",
            titleEmphasis: "stratégiques.",
            intro:
                "XV Capital Partners accompagne les dirigeants, sociétés et acteurs corporate dans leurs opérations de structuration stratégique, de préparation transactionnelle et de coordination opérationnelle — discrètement, avec la rigueur d'une boutique de strategic advisory.",
            footnote:
                "Chaque intervention est calibrée au contexte du dirigeant et menée dans la discrétion la plus stricte. Aucune mission ne quitte le cabinet.",
            cards: {
                acquisition: {
                    title: "Structuration d'Acquisition",
                    description:
                        "Accompagnement dans la structuration stratégique et opérationnelle de projets de reprise ou d'acquisition d'entreprise.",
                    bullets: [
                        "Préparation acquisition",
                        "Structuration holding",
                        "Coordination opérationnelle",
                        "Organisation gouvernance",
                        "Préparation post-transaction",
                    ],
                },
                realestate: {
                    title: "Structuration Immobilière",
                    description:
                        "Structuration d'opérations immobilières et organisation d'actifs corporate.",
                    bullets: [
                        "Architecture holding",
                        "Organisation patrimoniale",
                        "Structuration opérationnelle",
                        "Coordination des parties prenantes",
                        "Préparation gouvernance",
                    ],
                },
                duediligence: {
                    title: "Préparation Due Diligence",
                    description:
                        "Préparation et organisation préalable aux opérations stratégiques et phases de croissance.",
                    bullets: [
                        "Préparation data room",
                        "Structuration documentaire",
                        "Organisation KPI",
                        "Systèmes de reporting",
                        "Clarification opérationnelle",
                    ],
                },
                reorganisation: {
                    title: "Réorganisation Corporate",
                    description:
                        "Réorganisation stratégique et structuration des opérations d'entreprise.",
                    bullets: [
                        "Restructuration organisationnelle",
                        "Optimisation gouvernance",
                        "Redesign opérationnel",
                        "Structuration process",
                        "Préparation croissance",
                    ],
                },
                partnerships: {
                    title: "Partenariats Stratégiques",
                    description:
                        "Structuration et coordination de collaborations et initiatives stratégiques.",
                    bullets: [
                        "Structuration partenariat",
                        "Coordination des intervenants",
                        "Cadre opérationnel",
                        "Alignement gouvernance",
                        "Préparation déploiement",
                    ],
                },
                expansion: {
                    title: "Structuration Croissance & Expansion",
                    description:
                        "Préparation et structuration d'opérations de croissance et de développement.",
                    bullets: [
                        "Structuration expansion",
                        "Organisation croissance",
                        "Structuration groupe",
                        "Préparation intégration",
                        "Coordination stratégique",
                    ],
                },
            },
        },
        approche: {
            kicker: "Approche · Méthodologie",
            title1: "Quatre temps,",
            title2: "un accompagnement intégral.",
            lead:
                "XV Capital Partners conduit chaque opération selon une méthodologie claire — du cadrage initial à l'accompagnement post-closing — avec un seul interlocuteur du début à la fin.",
            stageObjective: "Objectif",
            stageDeliverables: "Livrables clés",
            audiencesKicker: "Cœur de cible",
            audiencesTitle1: "Les profils",
            audiencesTitle2: "que nous accompagnons.",
            audiencesIntro:
                "Le cabinet sélectionne ses mandats. Nos interventions se concentrent sur des opérations exigeantes — où la justesse de la structuration vaut plus que la vitesse.",
            opKicker: "Écosystème opérationnel",
            opTitle1: "Avocats, fiscalistes,",
            opTitle2: "experts-comptables.",
            opText:
                "Pour chaque mandat, le cabinet mobilise un sous-ensemble d'experts choisis parmi un cercle indépendant — droit des affaires, fiscalité, audit, due diligence, conformité, commercial et back-office.",
            discretionKicker: "Engagement",
            discretionTitle1: "La discrétion absolue, comme socle",
            discretionTitle2: "de toute relation.",
            discretionText:
                "Aucune mission, aucun nom, aucune opération ne quitte le cabinet. Notre clientèle nous recommande — c'est notre seule communication.",
            discretionCta: "Demander un entretien confidentiel",
        },
        ecosysteme: {
            kicker: "Écosystème international",
            title:
                "Un réseau d'expertises au service des situations complexes.",
            lead1: "Chaque mandat mobilise un cercle d'experts sélectionnés selon les enjeux du dossier.",
            lead2:
                "XV Capital Partners coordonne et fédère, autour de chaque opération, les compétences juridiques, financières, patrimoniales et sectorielles nécessaires à sa réussite.",
            introKicker: "Notre écosystème",
            introText1:
                "Nous privilégions des relations de confiance construites dans la durée avec des professionnels indépendants partageant les mêmes exigences d'excellence, de discrétion et d'engagement.",
            introText2:
                "Les expertises mobilisées varient selon la nature de chaque mission et sont coordonnées directement par le cabinet.",
            domains: [
                { title: "Droit des affaires & transmission", text: "Avocats d'affaires, structuration juridique, fiscalité, opérations de croissance et de transmission." },
                { title: "Ingénierie patrimoniale", text: "Fiscalistes, ingénierie patrimoniale, structuration internationale et optimisation de long terme." },
                { title: "Finance & financement", text: "Banques privées, banques d'affaires, dette privée, investisseurs et solutions de financement." },
                { title: "Expertise comptable & audit", text: "Experts-comptables, audit, valorisation, due diligence et accompagnement financier." },
                { title: "Immobilier & actifs réels", text: "Conseils immobiliers, structuration d'actifs, valorisation et stratégies patrimoniales." },
                { title: "Family Offices", text: "Accompagnement des familles entrepreneuriales, gouvernance et préservation patrimoniale." },
                { title: "Partenaires internationaux", text: "Correspondants et experts de confiance en Europe, Afrique, Moyen-Orient et Amérique du Nord." },
                { title: "Experts sectoriels", text: "Spécialistes indépendants mobilisés selon les enjeux spécifiques de chaque opération." },
            ],
            trustKicker: "Nos engagements",
            trustIntro:
                "Nous coordonnons un réseau sélectif de partenaires indépendants partageant nos exigences d'excellence, de confidentialité et d'engagement.",
            trustItems: [
                { kpi: "International", label: "Cercle de correspondants en Europe, Afrique, Moyen-Orient et Amérique du Nord" },
                { kpi: "Sélectif", label: "Experts mobilisés mandat par mandat selon les enjeux du dossier" },
                { kpi: "Indépendant", label: "Professionnels libres de tout conflit d'intérêts" },
                { kpi: "Confidentialité", label: "Engagement éthique absolu sur chaque opération coordonnée" },
                { kpi: "Excellence", label: "Reconnaissance dans chaque domaine d'expertise mobilisé" },
                { kpi: "Réactivité", label: "Disponibilité et coordination directe par le cabinet" },
            ],
            ctaLeftKicker: "Besoin d'être accompagné ?",
            ctaLeftText:
                "Discutons de vos enjeux avec un membre du cabinet, en toute confidentialité.",
            ctaMeeting: "Prendre rendez-vous",
            ctaRightKicker: "Une question sur notre écosystème ?",
        },
        collectivites: {
            kicker: "Acteurs institutionnels & territoires",
            title1: "Accompagner les territoires",
            title2: "dans leurs grandes opérations.",
            principlesKicker: "Quatre principes d'intervention",
            programHeadKicker: "Une approche dédiée aux territoires",
            programTitle1: "Une architecture",
            programTitle2: "sur-mesure",
            programTitle3: "pour chaque opération.",
            programText:
                "Le cabinet coordonne, pour chaque dossier territorial, l'écosystème international d'experts indépendants et de partenaires de long terme adapté — depuis le cadrage stratégique jusqu'à la sécurisation des conditions d'exécution.",
            programEligiblesLabel: "Périmètres d'intervention",
            conditionsKicker: "Notre approche",
            conditionsTitle1: "Un cadre",
            conditionsTitle2: "exigeant et discret.",
            whyFranceKicker: "Ancrage France",
            whyFranceTitle1: "Une signature publique",
            whyFranceTitle2: "de premier rang.",
            finalKicker: "Territoires · Premier contact",
            finalTitle1: "Un projet de territoire",
            finalTitle2: "à structurer ?",
            finalText:
                "Le cabinet conduit la phase de cadrage stratégique en parfaite confidentialité, avant toute coordination du réseau d'experts et de partenaires internationaux.",
            finalCta: "Demander un premier entretien",
        },
        articles: {
            kicker: "Articles",
            title1: "Notes d'analyses",
            title2: "pour les dirigeants exigeants.",
            lead:
                "Une lecture sobre, rigoureuse, écrite pour ceux qui préfèrent la profondeur à la tendance.",
            empty: "Aucun article publié pour le moment.",
            backToArticles: "Tous les articles",
            signature: "Signature",
            signatureFooter:
                "Cet article reflète l'approche du cabinet. Pour toute discussion confidentielle, prenez contact directement avec nous.",
            nextArticle: "Article suivant",
            exchangeKicker: "Échangeons",
            exchangeText:
                "Une question, un projet, un besoin d'expertise ?",
            readPiece: "Lire l'article",
            contactUs: "Prendre contact",
            byline: "Par",
            defaultAuthorRole: "Fondateur & Managing Partner",
            loading: "Chargement…",
        },
        contact: {
            kicker: "Premier entretien · Confidentiel",
            title1: "Initions",
            title2: "la conversation",
            lead:
                "Chaque mission s'ouvre par un entretien confidentiel d'évaluation. Décrivez-nous brièvement votre projet ; nous reprenons contact sous 48 heures ouvrées.",
            form: {
                name: "Nom complet",
                namePlaceholder: "Mme. / M.",
                email: "Adresse email",
                emailPlaceholder: "vous@cabinet.com",
                company: "Société / Structure",
                companyPlaceholder: "Optionnel",
                subject: "Nature du projet",
                subjectOptions: [
                    "Capital advisory",
                    "Structuration d'opération",
                    "Conseil stratégique",
                    "Advisory dirigeant",
                    "Réseau & introductions",
                    "Autre",
                ],
                message: "Votre message",
                messagePlaceholder:
                    "Quelques lignes sur votre projet, votre ambition, votre horizon.",
                consent:
                    "Je consens au traitement confidentiel de ces données pour la prise de contact. Je peux révoquer mon consentement à tout moment.",
                consentRequired: "Merci de consentir au traitement de vos données.",
                error: "Une erreur est survenue. Merci de réessayer.",
                success:
                    "Merci. Votre message nous est bien parvenu — nous reviendrons vers vous sous 48 heures ouvrées.",
                submitting: "Envoi en cours…",
                submit: "Envoyer ma demande",
            },
            info: {
                coordinates: "Coordonnées",
                byAppointment: "Sur rendez-vous uniquement",
                availability: "Disponibilité",
                availabilityText:
                    "Nous n'acceptons qu'un nombre limité de dossiers chaque trimestre.",
            },
        },
        espacePrive: {
            kicker: "Accès confidentiel · Authentification requise",
            title1: "Espace privé",
            title2: "XV Capital Partners.",
            lead:
                "L'accès aux espaces privés du cabinet est strictement réservé. Chaque connexion fait l'objet d'un enregistrement horodaté. Sélectionnez l'espace qui vous concerne.",
            cockpitKicker: "Cabinet · Cockpit",
            cockpitTitle: "Associés & collaborateurs",
            cockpitText:
                "Cockpit interne de pilotage des opérations confidentielles — dossiers, partenaires, documents, notes. Accès strictement réservé aux membres du cabinet.",
            cockpitCta: "Accéder au cockpit",
            clientKicker: "Clients · Suivi de dossier",
            clientTitle: "Espace client confidentiel",
            clientText:
                "Suivi en temps réel de votre dossier — étapes réalisées, prochaines actions, documents partagés, comptes rendus validés.",
            clientSub: "Accès délivré nominativement par le cabinet, sur invitation.",
            clientCta: "Accéder à mon espace",
            clientHint:
                "Vous êtes client du cabinet et avez reçu un lien d'accès personnel ? Vous pouvez vous connecter directement avec vos identifiants.",
            securityNote: "Sécurité · TOTP · Sessions horodatées · Journal d'audit",
        },
        mentions: {
            kicker: "Mentions légales",
            title: "Informations légales et juridiques.",
            lead:
                "XV Capital Partners est une marque exploitée par XV INVESTISSEMENT HOLDING GROUP, société par actions simplifiée de droit français.",
            unavailable: "Contenu indisponible",
            loading: "Chargement…",
            sections: {
                editor: "Éditeur du site",
                director: "Directeur de la publication",
                hosting: "Hébergement",
                ip: "Propriété intellectuelle",
                responsibility: "Responsabilité",
                activity: "Activité",
                hyperlinks: "Liens hypertextes",
                law: "Droit applicable",
            },
            labels: {
                brand: "Marque",
                company: "Société exploitante",
                legalForm: "Forme juridique",
                siren: "SIREN",
                siret: "SIRET du siège",
                hq: "Siège social",
                createdAt: "Date de création",
                category: "Catégorie d'entreprise",
                activity: "Activité",
                name: "Nom",
                address: "Adresse",
                contact: "Contact",
            },
            hostFallback1: "Informations d'hébergement disponibles sur demande à ",
            hostFallback2: ".",
            ipP1:
                "L'ensemble des textes, visuels, logos, éléments graphiques, bases de données, concepts, documents et contenus présents sur le site sont protégés par le Code de la propriété intellectuelle.",
            ipP2:
                "Toute reproduction, représentation, diffusion ou exploitation, totale ou partielle, sans autorisation écrite préalable est interdite.",
            respP1:
                "Les informations publiées sur le site sont fournies à titre informatif et ne constituent ni une offre contractuelle, ni un conseil juridique, fiscal, comptable ou financier personnalisé.",
            respP2Prefix: " intervient comme cabinet de conseil stratégique et de structuration et coordonne, lorsque cela est nécessaire, les expertises adaptées aux besoins de ses clients.",
            actP1:
                "Le site présente les activités d'accompagnement stratégique, de structuration d'opérations, d'ingénierie financière, de développement d'entreprise, de transmission, de coordination d'expertises et d'accompagnement patrimonial.",
            actP2Suffix:
                " n'est ni une banque, ni un établissement de crédit, ni un prestataire de services d'investissement, et ne collecte directement aucune épargne du public.",
            hyperlinks1: "Les liens externes présents sur le site sont fournis à titre informatif. ",
            hyperlinks2: " ne saurait être responsable du contenu des sites tiers.",
            lawP1: "Le présent site est soumis au droit français.",
            lawP2:
                "Tout litige relatif à son utilisation relève de la compétence des juridictions françaises compétentes.",
        },
        cgu: {
            kicker: "Conditions Générales d'Utilisation",
            title: "Cadre d'usage du site, en toute transparence.",
            lead1: "Les présentes Conditions Générales d'Utilisation définissent les modalités d'accès et d'utilisation du site internet de ",
            lead2: ".",
            section1Title: "1. Objet",
            section1P1Pre: "Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») définissent les modalités d'accès et d'utilisation du site internet de ",
            section1P1Post: ".",
            section1P2: "Le site a pour vocation de présenter les activités du cabinet et de permettre une prise de contact confidentielle avec les équipes.",
            section2Title: "2. Éditeur",
            section2P1Pre1: "",
            section2P1Mid: " est une marque exploitée par ",
            section2P1Post: ", société par actions simplifiée de droit français.",
            section2P2Pre: "Les informations juridiques (SIREN, siège social, directeur de la publication) sont disponibles sur la page ",
            section2P2Link: "Mentions légales",
            section2P2Post: ".",
            section3Title: "3. Nature des informations",
            section3P: "Les informations diffusées sur le site sont fournies à titre exclusivement informatif. Elles ne constituent en aucun cas :",
            section3Items: [
                "ni une consultation juridique,",
                "ni une consultation fiscale,",
                "ni un conseil comptable,",
                "ni un conseil en investissement personnalisé,",
                "ni une offre au public,",
                "ni une garantie d'obtention d'un financement ou de réalisation d'une opération.",
            ],
            section4Title: "4. Relation contractuelle",
            section4P1Pre: "La consultation du site ne crée aucune relation contractuelle entre l'utilisateur et ",
            section4P1Post: ".",
            section4P2: "Toute mission fait l'objet d'un échange préalable, d'une analyse, et, le cas échéant, d'une lettre de mission ou d'un contrat spécifique.",
            section5Title: "5. Obligation de moyens",
            section5PPre: "",
            section5PMid: " intervient dans le cadre d'une obligation de moyens. Le cabinet met en œuvre toutes les diligences nécessaires mais ne garantit pas :",
            section5Items: [
                "l'obtention d'un financement,",
                "la conclusion d'une opération,",
                "la réalisation d'une acquisition,",
                "la réussite d'une structuration capitalistique,",
                "ni la signature d'un partenariat.",
            ],
            section6Title: "6. Utilisation du site",
            section6P: "L'utilisateur s'engage à utiliser le site conformément à la réglementation applicable et aux présentes CGU. Il s'interdit notamment :",
            section6Items: [
                "toute utilisation frauduleuse,",
                "toute tentative d'intrusion,",
                "toute extraction automatisée des données,",
                "toute reproduction non autorisée des contenus.",
            ],
            section7Title: "7. Propriété intellectuelle",
            section7P1: "L'ensemble des éléments du site est protégé par le Code de la propriété intellectuelle : logo, identité visuelle, textes, méthodologie, concepts, documents, illustrations et architecture du site.",
            section7P2: "Toute reproduction totale ou partielle est interdite sans autorisation écrite préalable.",
            section8Title: "8. Disponibilité",
            section8PPre: "",
            section8PPost: " s'efforce d'assurer la disponibilité du site mais ne garantit pas une disponibilité permanente. Le site peut être interrompu pour maintenance ou mise à jour, sans préavis.",
            section9Title: "9. Responsabilité",
            section9PPre: "",
            section9PPost: " ne pourra être tenu responsable notamment :",
            section9Items: [
                "des décisions prises par les utilisateurs sur la base des informations publiées,",
                "des contenus des sites tiers,",
                "des interruptions techniques,",
                "des conséquences liées à une mauvaise utilisation du site.",
            ],
            section10Title: "10. Données personnelles",
            section10Pre: "Les modalités de traitement de vos données personnelles sont décrites dans la ",
            section10Link: "Politique de confidentialité",
            section10Post: ", qui détaille également la politique de cookies.",
            section11Title: "11. Modification des CGU",
            section11PPre: "",
            section11PPost: " peut modifier les présentes Conditions Générales d'Utilisation à tout moment. La version applicable est celle publiée sur le site à la date de consultation.",
            section12Title: "12. Droit applicable",
            section12P1: "Les présentes CGU sont régies par le droit français.",
            section12P2: "Tout litige relatif à l'utilisation du site relève de la compétence des juridictions françaises compétentes.",
        },
        confidentialite: {
            kicker: "Politique de confidentialité",
            title: "Vos données personnelles. Notre engagement.",
            lead:
                "Conformément au Règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés, nous garantissons la confidentialité, la sécurité et le respect des droits liés à vos données personnelles.",
            s1Title: "1. Finalités des traitements",
            s1P:
                "Les données personnelles collectées sur ce site sont traitées pour : répondre aux demandes d'entretien, transmettre des informations à votre demande, assurer le suivi des relations professionnelles, répondre aux obligations légales et fiscales, et améliorer l'expérience de navigation.",
            s2Title: "2. Données collectées",
            s2P1:
                "Nous ne collectons que les données strictement nécessaires : nom, prénom, fonction, société, adresse e-mail, téléphone, ainsi que le contenu des messages que vous nous adressez.",
            s2P2:
                "Aucune donnée sensible (origine, religion, opinions, santé…) n'est collectée.",
            s3Title: "3. Formulaires de contact",
            s3P:
                "Les formulaires de contact présents sur le site transmettent vos demandes par voie sécurisée au cabinet. Aucune donnée n'est cédée à des tiers à des fins commerciales.",
            s4Title: "4. Cookies",
            s4P:
                "Le site utilise uniquement des cookies techniques strictement nécessaires à son fonctionnement. Aucun traceur publicitaire, aucun outil d'analyse comportementale tiers.",
            s5Title: "5. Base légale des traitements",
            s5P:
                "Les traitements reposent sur l'intérêt légitime du cabinet à répondre aux demandes reçues, sur l'exécution des relations contractuelles, et — le cas échéant — sur le consentement explicite de l'utilisateur.",
            s6Title: "6. Durée de conservation",
            s6P:
                "Données de prospection : 3 ans à compter du dernier contact. Données contractuelles : durée de la relation augmentée des délais légaux de conservation. Données comptables : 10 ans.",
            s7Title: "7. Destinataires des données",
            s7P:
                "Les données sont strictement réservées aux membres habilités du cabinet. Aucune transmission à des tiers, hors prestataires techniques liés par une obligation contractuelle de confidentialité (hébergeur, messagerie sécurisée).",
            s8Title: "8. Sécurité des données",
            s8P:
                "Les données sont stockées sur des serveurs sécurisés, protégées par des mesures techniques et organisationnelles appropriées : chiffrement, contrôle d'accès strict, sauvegardes chiffrées, journalisation des accès.",
            s9Title: "9. Vos droits",
            s9P:
                "Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :",
            s9Items: [
                "Droit d'accès — obtenir copie de vos données",
                "Droit de rectification — corriger des informations erronées",
                "Droit d'opposition — refuser certains traitements",
                "Droit à l'effacement — supprimer vos données",
                "Droit à la limitation — restreindre l'usage",
                "Droit à la portabilité — récupérer vos données",
            ],
            s9PExerciseA: "Vous pouvez exercer ces droits à tout moment via le formulaire ci-dessous ou par courriel à ",
            s9PExerciseB: ".",
            requestTypes: [
                { v: "access", l: "Droit d'accès" },
                { v: "rectification", l: "Droit de rectification" },
                { v: "opposition", l: "Droit d'opposition" },
                { v: "erasure", l: "Droit à l'effacement" },
                { v: "limitation", l: "Droit à la limitation" },
                { v: "portability", l: "Droit à la portabilité" },
                { v: "other", l: "Autre demande" },
            ],
            formKicker: "Exercer mes droits",
            formTitle: "Formulaire d'exercice des droits RGPD.",
            formIntro:
                "Nous traitons toutes les demandes dans un délai d'un mois. Une pièce d'identité peut vous être demandée.",
            formFullName: "Nom complet",
            formEmail: "Adresse e-mail",
            formType: "Nature de la demande",
            formMessage: "Précisez votre demande",
            formMessagePlaceholder: "Décrivez précisément votre demande…",
            formError: "Une erreur est survenue. Réessayez ou contactez-nous par e-mail.",
            formSubmit: "Envoyer ma demande",
            formSubmitting: "Envoi en cours…",
            successKicker: "Demande enregistrée",
            successTitle: "Votre demande nous est parvenue.",
            successText:
                "Nous vous répondrons dans un délai d'un mois à l'adresse indiquée. Un courriel de confirmation a été envoyé à notre Délégué à la protection des données.",
        },
        faq: {
            kicker: "Questions fréquentes",
            title1: "Les questions",
            title2: "que l'on nous pose souvent.",
        },
    },
    en: {
        nav: {
            home: "Home",
            about: "About",
            expertises: "Expertise",
            approach: "Approach",
            ecosystem: "Ecosystem",
            collectivites: "Public sector",
            articles: "Insights",
            contact: "Contact",
            private: "Private access",
            cta: "Request a meeting",
        },
        common: {
            discover: "Discover",
            learnMore: "Learn more",
            readArticle: "Read the piece",
            requestMeeting: "Request a confidential meeting",
            allArticles: "All articles",
            backToArticles: "All articles",
            language: "Language",
            seeMore: "See more",
            loading: "Loading…",
            noArticles: "No articles published yet.",
            discoverApproach: "Discover our approach",
            initiateConversation: "Initiate the conversation",
            scroll: "Scroll",
            by: "By",
            allRightsReserved: "All rights reserved",
            legal: "Legal",
            privacy: "Privacy",
            terms: "Terms",
        },
        footer: {
            navigation: "Navigation",
            contact: "Get in touch",
            description:
                "An independent strategic advisory and financial architecture boutique — at the intersection of corporate development and international expert networks.",
            tagline: "Strategic advisory · Financial architecture",
            requestInterview: "Request a meeting",
            legal:
                "XV Capital Partners is an independent strategic advisory and financial architecture boutique. The firm carries out no asset management, no investment placement, no regulated investment advice and no financial intermediation. It engages in no public solicitation of savings and no banking or financial canvassing within the meaning of articles L. 341-1 et seq. of the French Monetary and Financial Code.",
        },
        home: {
            heroBadge: "Strategic advisory · French Riviera",
            cta1: "Request a meeting",
            cta2: "Our approach",
            ctaExpertises: "Explore our expertise",
            hero: {
                badge: "Strategic advisory · French Riviera",
                title: "The financial architecture",
                titleEmphasis: "of rare ambitions.",
                subtitle:
                    "XV Capital Partners supports executives and investors in the preparation and structuring of acquisitions, financings and strategic partnerships in France and internationally. We coordinate the legal, tax and financial expertise suited to each operation and mobilise the relevant partners.",
            },
            manifesto: {
                kicker: "Manifesto · Independent boutique",
                line1: "The operations that matter",
                line2: "are not handled alone,",
                line3: "nor at scale.",
                lead: "A legal, tax and financial architecture calibrated to the measure of an ambition.",
                p1: "Around each engagement, XV Capital Partners coordinates a restricted circle of experts — corporate law, private and corporate tax, audit, due diligence, governance, investor relations — selected for the discretion and rigour of their execution.",
                p2: "A rigorous preparation, a responsive coordination and a timeline tailored to the stakes of each operation.",
            },
            stats: {
                kicker: "Selectivity · Discretion · Long-term vision",
                items: [
                    { value: 8, suffix: "", label: "International financial hubs" },
                    { value: 4, suffix: "", label: "Continents covered by the network" },
                    { value: 20, suffix: " yrs+", label: "On-the-ground experience" },
                ],
            },
            services: {
                kicker: "Practices",
                title1: "One engagement,",
                title2: "four dimensions.",
                intro:
                    "Each mission combines, to varying degrees, these four dimensions — coordinated in-house and backed by a network of independent experts.",
                items: [
                    {
                        kicker: "I",
                        title: "Financial & wealth architecture",
                        desc: "Design, tailored to each executive, of a coherent financial and wealth architecture — coordinated, discreet, thought for the long term.",
                    },
                    {
                        kicker: "II",
                        title: "Growth & transmission operations",
                        desc: "Strategic support across development, growth and transmission operations — preserving the executive's governance and vision.",
                    },
                    {
                        kicker: "III",
                        title: "Financing & structuring engineering",
                        desc: "Financing solutions calibrated to wealth and entrepreneurial stakes — coordination of independent experts and international partners.",
                    },
                    {
                        kicker: "IV",
                        title: "International network of experts",
                        desc: "Coordination of an international ecosystem of experts and private finance — mobilised, mandate by mandate, within a restricted and confidential circle.",
                    },
                ],
            },
            collectivitesSpot: {
                kicker: "Institutional actors & territories",
                title1: "Supporting",
                title2: "territories.",
                text: "For public and institutional actors, the firm coordinates an international ecosystem of experts and long-term financial partners — dedicated to structuring large-scale operations in service of territorial development.",
                tiles: [
                    { v: "Long term", l: "Horizon" },
                    { v: "International", l: "Network" },
                    { v: "Selective", l: "Mandates" },
                    { v: "Confidential", l: "Operating mode" },
                ],
            },
            presence: {
                kicker: "International presence",
                title1: "An independent firm,",
                title2: "an international network.",
                text: "XV Capital Partners coordinates, as each mission requires, a network of independent partners and experts internationally.",
            },
            insights: {
                kicker: "Insights",
                title1: "Analytical notes,",
                title2: "written for executives.",
                seeAll: "Read all notes",
            },
            ctaFinal: {
                kicker: "First meeting · Confidential",
                title1: "A demanding operation?",
                title2: "Let's discuss, behind closed doors.",
                text: "Every mission begins with a confidential evaluation meeting. The firm accepts only a limited number of mandates each quarter.",
            },
        },
        about: {
            kicker: "About",
            title: "A boutique founded on",
            titleEmphasis: "private finance",
            titleSuffix: "and strategic support.",
            lead:
                "XV Capital Partners is an independent strategic advisory boutique dedicated to the strategic and financial operations of executives, private investors and family offices. Discreet by choice, demanding by method.",
            founderKicker: "Founder",
            founderRole: "Founder & Managing Partner",
            founderName: "Joel Kotho",
            founderBody: {
                p1_part1: "Trained in",
                p1_part2: "accounting and management",
                p1_part3: ", his career has taken him across the worlds of",
                p1_part4: "banking, insurance, industry and entrepreneurship",
                p1_part5:
                    " — giving him a concrete understanding of the financial, wealth and strategic challenges facing decision-makers.",
                p2_part1: "Over the years, he has built a",
                p2_part2: "trusted network",
                p2_part3:
                    " bringing together executives, investors, family offices, strategic partners and institutional actors across Europe, Africa, the Middle East and North America. This ability to connect the right expertise and the right interlocutors around complex situations is now one of the foundations of the XV Capital Partners approach.",
                p3_part1: "In parallel with this professional career, he represents",
                p3_part2: "Congo at international level in rugby",
                p3_part3:
                    ". This high-level experience passes on a lasting culture of discipline, commitment, preparation and collective responsibility, which continues to inspire his approach to complex projects and demanding environments.",
                p4_part1:
                    "His conviction is simple: the most sensitive operations demand more than technical expertise. They require the ability to",
                p4_part2: "coordinate multiple interests",
                p4_part3:
                    ", create the right conditions of trust and anchor every decision in a long-term vision.",
                p5_part1:
                    "Through XV Capital Partners, Joel Kotho supports growth, transmission, wealth structuring and international development operations with the same demand:",
                p5_part2:
                    "commitment, discretion, selectivity and long-term vision",
                p5_part3: ".",
            },
            institutionsLabel: "Institutional background",
            pillarsKicker: "A personal signature",
            pillarsTitle1: "Four principles,",
            pillarsTitle2: "a single way.",
            pillarsIntro:
                "The codes learned in high-level sport structure the way the firm conducts every mandate today.",
            valuesKicker: "Four principles",
            valuesTitle1: "What guides",
            valuesTitle2: "every mandate.",
            valuesIntro:
                "Trust is not asked for. It is earned, file by file, through uncompromising commitment and absolute discretion.",
            values: [
                {
                    kicker: "I",
                    title: "Discretion",
                    desc: "Information is shared only with the stakeholders required for the mission, within a confidentiality framework agreed with the client.",
                },
                {
                    kicker: "II",
                    title: "Rigour",
                    desc: "A methodology drawn from international finance and executive advisory. Anticipate, structure, secure.",
                },
                {
                    kicker: "III",
                    title: "Selectivity",
                    desc: "A limited number of mandates per quarter. Full, undiluted attention on every accepted operation.",
                },
                {
                    kicker: "IV",
                    title: "Network",
                    desc: "An international circle of family offices, private investors and independent experts, built over two decades.",
                },
            ],
            ecoKicker: "A circle of experts",
            ecoTitle1: "Beyond a team,",
            ecoTitle2: "an ecosystem.",
            ecoText:
                "XV Capital Partners coordinates a restricted circle of independent experts — corporate law, tax, audit, due diligence, governance, investor relations — mobilised bespoke for each operation.",
            finalTitle1: "An operation to structure",
            finalTitle2: "with rigour?",
            finalCta: "Confidential first meeting",
        },
        expertises: {
            kicker: "Practices · Strategic advisory",
            title1: "Three practices,",
            title2: "a bespoke architecture.",
            lead:
                "XV Capital Partners operates at the intersection of corporate development, wealth structuring and international expert networks — each mandate calibrated as closely as possible to the context of the executive or institutional actor supported.",
            labelIntervention: "Scope",
            horizontalKicker: "Expertise mobilised",
            horizontalTitle1: "Ten areas of expertise,",
            horizontalTitle2: "a single interlocutor.",
            horizontalIntro:
                "Each operation mobilises a subset of these expertises, coordinated in-house and backed by a circle of tax lawyers, business law specialists, chartered accountants and back-office professionals.",
            horizontalItems: [
                { n: "I", title: "Strategy & long-term vision" },
                { n: "II", title: "Financial & wealth architecture" },
                { n: "III", title: "Legal engineering" },
                { n: "IV", title: "Private & corporate tax" },
                { n: "V", title: "Accounting & audit" },
                { n: "VI", title: "Due diligence & analysis" },
                { n: "VII", title: "Coordination of independent experts" },
                { n: "VIII", title: "International structuring" },
                { n: "IX", title: "Governance & compliance" },
                { n: "X", title: "Executive & family office advisory" },
            ],
            collectivitesTeaseKicker: "Institutional actors & territories",
            collectivitesTeaseTitle1: "Supporting territories",
            collectivitesTeaseTitle2: "in their major operations.",
            collectivitesTeaseText:
                "For local authorities, public operators and economic development actors, the firm coordinates an international ecosystem of experts and long-term financial partners — calibrated to the structuring nature of each project.",
        },
        operations: {
            kicker: "Areas of intervention",
            title: "Strategic",
            titleEmphasis: "operations.",
            intro:
                "XV Capital Partners supports executives, companies and corporate stakeholders in their strategic structuring, transaction preparation and operational coordination — discreetly, with the rigour of a strategic advisory boutique.",
            footnote:
                "Each engagement is calibrated to the executive's context and conducted with the utmost discretion. No mission ever leaves the firm.",
            cards: {
                acquisition: {
                    title: "Acquisition Structuring",
                    description:
                        "Support across the strategic and operational structuring of takeover or acquisition projects.",
                    bullets: [
                        "Acquisition preparation",
                        "Holding structuring",
                        "Operational coordination",
                        "Governance setup",
                        "Post-transaction readiness",
                    ],
                },
                realestate: {
                    title: "Real Estate Structuring",
                    description:
                        "Structuring of real estate operations and corporate asset organisation.",
                    bullets: [
                        "Holding architecture",
                        "Wealth organisation",
                        "Operational structuring",
                        "Stakeholder coordination",
                        "Governance preparation",
                    ],
                },
                duediligence: {
                    title: "Due Diligence Readiness",
                    description:
                        "Preparation and organisation ahead of strategic operations and growth phases.",
                    bullets: [
                        "Data room preparation",
                        "Documentary structuring",
                        "KPI organisation",
                        "Reporting systems",
                        "Operational clarification",
                    ],
                },
                reorganisation: {
                    title: "Corporate Reorganisation",
                    description:
                        "Strategic reorganisation and structuring of corporate operations.",
                    bullets: [
                        "Organisational restructuring",
                        "Governance optimisation",
                        "Operational redesign",
                        "Process structuring",
                        "Growth preparation",
                    ],
                },
                partnerships: {
                    title: "Strategic Partnerships",
                    description:
                        "Structuring and coordination of strategic collaborations and initiatives.",
                    bullets: [
                        "Partnership structuring",
                        "Stakeholder coordination",
                        "Operational framework",
                        "Governance alignment",
                        "Deployment readiness",
                    ],
                },
                expansion: {
                    title: "Growth & Expansion Structuring",
                    description:
                        "Preparation and structuring of growth and development operations.",
                    bullets: [
                        "Expansion structuring",
                        "Growth organisation",
                        "Group structuring",
                        "Integration readiness",
                        "Strategic coordination",
                    ],
                },
            },
        },
        approche: {
            kicker: "Approach · Methodology",
            title1: "Four stages,",
            title2: "a complete support.",
            lead:
                "XV Capital Partners conducts each operation along a clear methodology — from initial scoping to post-closing support — with a single interlocutor from start to finish.",
            stageObjective: "Objective",
            stageDeliverables: "Key deliverables",
            audiencesKicker: "Core clientele",
            audiencesTitle1: "The profiles",
            audiencesTitle2: "we support.",
            audiencesIntro:
                "The firm selects its mandates. Our engagements focus on demanding operations — where the precision of the structuring matters more than speed.",
            opKicker: "Operational ecosystem",
            opTitle1: "Lawyers, tax specialists,",
            opTitle2: "chartered accountants.",
            opText:
                "For each mandate, the firm mobilises a subset of experts selected from an independent circle — business law, tax, audit, due diligence, compliance, commercial and back-office.",
            discretionKicker: "Commitment",
            discretionTitle1: "Absolute discretion, as the foundation",
            discretionTitle2: "of every relationship.",
            discretionText:
                "No mission, no name, no operation ever leaves the firm. Our clientele recommends us — that is our only communication.",
            discretionCta: "Request a confidential meeting",
        },
        ecosysteme: {
            kicker: "International ecosystem",
            title: "A network of expertise serving complex situations.",
            lead1: "Each mandate mobilises a circle of experts selected according to the stakes of the file.",
            lead2:
                "XV Capital Partners coordinates and federates, around each operation, the legal, financial, wealth and sector expertise necessary for its success.",
            introKicker: "Our ecosystem",
            introText1:
                "We favour trusted relationships built over time with independent professionals sharing the same demands for excellence, discretion and commitment.",
            introText2:
                "The expertise mobilised varies according to the nature of each mission and is coordinated directly by the firm.",
            domains: [
                { title: "Business law & transmission", text: "Business lawyers, legal structuring, tax, growth and transmission operations." },
                { title: "Wealth engineering", text: "Tax specialists, wealth engineering, international structuring and long-term optimisation." },
                { title: "Finance & financing", text: "Private banks, investment banks, private debt, investors and financing solutions." },
                { title: "Accounting & audit", text: "Chartered accountants, audit, valuation, due diligence and financial support." },
                { title: "Real estate & real assets", text: "Real estate advisors, asset structuring, valuation and wealth strategies." },
                { title: "Family Offices", text: "Support for entrepreneurial families, governance and wealth preservation." },
                { title: "International partners", text: "Trusted correspondents and experts in Europe, Africa, the Middle East and North America." },
                { title: "Sector experts", text: "Independent specialists mobilised according to the specific stakes of each operation." },
            ],
            trustKicker: "Our commitments",
            trustIntro:
                "We coordinate a selective network of independent partners sharing our demands for excellence, confidentiality and commitment.",
            trustItems: [
                { kpi: "International", label: "Circle of correspondents in Europe, Africa, the Middle East and North America" },
                { kpi: "Selective", label: "Experts mobilised mandate by mandate according to the stakes of the file" },
                { kpi: "Independent", label: "Professionals free from any conflict of interest" },
                { kpi: "Confidentiality", label: "Absolute ethical commitment on every coordinated operation" },
                { kpi: "Excellence", label: "Recognition in each area of expertise mobilised" },
                { kpi: "Responsiveness", label: "Availability and direct coordination by the firm" },
            ],
            ctaLeftKicker: "Need support?",
            ctaLeftText:
                "Let's discuss your challenges with a member of the firm, in complete confidentiality.",
            ctaMeeting: "Book a meeting",
            ctaRightKicker: "A question about our ecosystem?",
        },
        collectivites: {
            kicker: "Institutional actors & territories",
            title1: "Supporting territories",
            title2: "in their major operations.",
            principlesKicker: "Four intervention principles",
            programHeadKicker: "A dedicated approach for territories",
            programTitle1: "A",
            programTitle2: "bespoke",
            programTitle3: "architecture for every operation.",
            programText:
                "For each territorial file, the firm coordinates the adapted international ecosystem of independent experts and long-term partners — from strategic scoping through to securing execution conditions.",
            programEligiblesLabel: "Scopes of intervention",
            conditionsKicker: "Our approach",
            conditionsTitle1: "A",
            conditionsTitle2: "demanding and discreet framework.",
            whyFranceKicker: "French anchoring",
            whyFranceTitle1: "A first-rank",
            whyFranceTitle2: "public signature.",
            finalKicker: "Territories · First contact",
            finalTitle1: "A territorial project",
            finalTitle2: "to structure?",
            finalText:
                "The firm conducts the strategic scoping phase in complete confidentiality, prior to any coordination of the network of experts and international partners.",
            finalCta: "Request a first meeting",
        },
        articles: {
            kicker: "Insights",
            title1: "Analytical notes",
            title2: "for demanding executives.",
            lead:
                "A sober, rigorous reading, written for those who prefer depth over trend.",
            empty: "No articles published yet.",
            backToArticles: "All articles",
            signature: "Signature",
            signatureFooter:
                "This article reflects the firm's approach. For any confidential discussion, please get in touch with us directly.",
            nextArticle: "Next article",
            exchangeKicker: "Let's talk",
            exchangeText:
                "A question, a project, a need for expertise?",
            readPiece: "Read the piece",
            contactUs: "Get in touch",
            byline: "By",
            defaultAuthorRole: "Founder & Managing Partner",
            loading: "Loading…",
        },
        contact: {
            kicker: "First meeting · Confidential",
            title1: "Let's start",
            title2: "the conversation",
            lead:
                "Every mission begins with a confidential evaluation meeting. Tell us briefly about your project; we will get back to you within 48 business hours.",
            form: {
                name: "Full name",
                namePlaceholder: "Ms. / Mr.",
                email: "Email address",
                emailPlaceholder: "you@firm.com",
                company: "Company / Structure",
                companyPlaceholder: "Optional",
                subject: "Project type",
                subjectOptions: [
                    "Capital advisory",
                    "Operation structuring",
                    "Strategic advisory",
                    "Executive advisory",
                    "Network & introductions",
                    "Other",
                ],
                message: "Your message",
                messagePlaceholder:
                    "A few lines about your project, your ambition, your horizon.",
                consent:
                    "I consent to the confidential processing of this data for the purpose of making contact. I may withdraw my consent at any time.",
                consentRequired: "Please consent to the processing of your data.",
                error: "An error occurred. Please try again.",
                success:
                    "Thank you. Your message has reached us — we will get back to you within 48 business hours.",
                submitting: "Sending…",
                submit: "Send my request",
            },
            info: {
                coordinates: "Contact",
                byAppointment: "By appointment only",
                availability: "Availability",
                availabilityText:
                    "We only accept a limited number of files each quarter.",
            },
        },
        espacePrive: {
            kicker: "Confidential access · Authentication required",
            title1: "Private space",
            title2: "XV Capital Partners.",
            lead:
                "Access to the firm's private spaces is strictly reserved. Every connection is time-stamped and recorded. Please select the space that concerns you.",
            cockpitKicker: "Firm · Cockpit",
            cockpitTitle: "Partners & collaborators",
            cockpitText:
                "Internal cockpit for the piloting of confidential operations — files, partners, documents, notes. Access strictly reserved for members of the firm.",
            cockpitCta: "Access the cockpit",
            clientKicker: "Clients · File tracking",
            clientTitle: "Confidential client space",
            clientText:
                "Real-time tracking of your file — completed stages, next actions, shared documents, validated reports.",
            clientSub: "Access delivered personally by the firm, on invitation.",
            clientCta: "Access my space",
            clientHint:
                "Are you a client of the firm and have received a personal access link? You can log in directly with your credentials.",
            securityNote: "Security · TOTP · Time-stamped sessions · Audit log",
        },
        mentions: {
            kicker: "Legal notice",
            title: "Legal and corporate information.",
            lead:
                "XV Capital Partners is a brand operated by XV INVESTISSEMENT HOLDING GROUP, a simplified joint-stock company under French law.",
            unavailable: "Content unavailable",
            loading: "Loading…",
            sections: {
                editor: "Site publisher",
                director: "Publication director",
                hosting: "Hosting",
                ip: "Intellectual property",
                responsibility: "Liability",
                activity: "Activity",
                hyperlinks: "Hyperlinks",
                law: "Applicable law",
            },
            labels: {
                brand: "Brand",
                company: "Operating company",
                legalForm: "Legal form",
                siren: "SIREN",
                siret: "Head office SIRET",
                hq: "Head office",
                createdAt: "Date of creation",
                category: "Company category",
                activity: "Activity",
                name: "Name",
                address: "Address",
                contact: "Contact",
            },
            hostFallback1: "Hosting information available on request at ",
            hostFallback2: ".",
            ipP1:
                "All texts, visuals, logos, graphic elements, databases, concepts, documents and content on the site are protected by the Intellectual Property Code.",
            ipP2:
                "Any reproduction, representation, distribution or exploitation, in whole or in part, without prior written authorisation is prohibited.",
            respP1:
                "The information published on the site is provided for informational purposes and does not constitute a contractual offer or personalised legal, tax, accounting or financial advice.",
            respP2Prefix: " acts as a strategic advisory and structuring firm and coordinates, where necessary, the expertise suited to the needs of its clients.",
            actP1:
                "The site presents the activities of strategic support, operation structuring, financial engineering, corporate development, transmission, expert coordination and wealth support.",
            actP2Suffix:
                " is neither a bank, nor a credit institution, nor an investment services provider, and does not directly collect any public savings.",
            hyperlinks1: "External links on the site are provided for informational purposes. ",
            hyperlinks2: " cannot be held responsible for the content of third-party sites.",
            lawP1: "This site is governed by French law.",
            lawP2:
                "Any dispute relating to its use falls within the jurisdiction of the competent French courts.",
        },
        cgu: {
            kicker: "Terms of Use",
            title: "Framework for using the site, with full transparency.",
            lead1: "These Terms of Use define the terms of access to and use of the website of ",
            lead2: ".",
            section1Title: "1. Purpose",
            section1P1Pre: "These Terms of Use (hereinafter the \"Terms\") define the terms of access to and use of the website of ",
            section1P1Post: ".",
            section1P2: "The purpose of the site is to present the firm's activities and to allow confidential contact with the teams.",
            section2Title: "2. Publisher",
            section2P1Pre1: "",
            section2P1Mid: " is a brand operated by ",
            section2P1Post: ", a simplified joint-stock company under French law.",
            section2P2Pre: "Legal information (SIREN, head office, publication director) is available on the ",
            section2P2Link: "Legal notice",
            section2P2Post: " page.",
            section3Title: "3. Nature of the information",
            section3P: "The information published on the site is provided for informational purposes only. It in no way constitutes:",
            section3Items: [
                "legal consultation,",
                "tax consultation,",
                "accounting advice,",
                "personalised investment advice,",
                "a public offering,",
                "a guarantee of obtaining financing or carrying out an operation.",
            ],
            section4Title: "4. Contractual relationship",
            section4P1Pre: "Consulting the site does not create any contractual relationship between the user and ",
            section4P1Post: ".",
            section4P2: "Every mission is subject to a prior exchange, an analysis, and, where appropriate, an engagement letter or a specific contract.",
            section5Title: "5. Obligation of means",
            section5PPre: "",
            section5PMid: " acts under an obligation of means. The firm applies all necessary diligence but does not guarantee:",
            section5Items: [
                "the obtaining of financing,",
                "the conclusion of an operation,",
                "the completion of an acquisition,",
                "the success of a capital structuring,",
                "or the signing of a partnership.",
            ],
            section6Title: "6. Use of the site",
            section6P: "The user undertakes to use the site in accordance with the applicable regulations and the present Terms. They shall refrain in particular from:",
            section6Items: [
                "any fraudulent use,",
                "any attempt at intrusion,",
                "any automated extraction of data,",
                "any unauthorised reproduction of content.",
            ],
            section7Title: "7. Intellectual property",
            section7P1: "All elements of the site are protected by the Intellectual Property Code: logo, visual identity, texts, methodology, concepts, documents, illustrations and site architecture.",
            section7P2: "Any total or partial reproduction is prohibited without prior written authorisation.",
            section8Title: "8. Availability",
            section8PPre: "",
            section8PPost: " endeavours to ensure the availability of the site but does not guarantee permanent availability. The site may be interrupted for maintenance or updates, without notice.",
            section9Title: "9. Liability",
            section9PPre: "",
            section9PPost: " cannot be held liable, in particular, for:",
            section9Items: [
                "decisions made by users on the basis of the information published,",
                "the content of third-party sites,",
                "technical interruptions,",
                "consequences linked to misuse of the site.",
            ],
            section10Title: "10. Personal data",
            section10Pre: "The terms of processing of your personal data are described in the ",
            section10Link: "Privacy Policy",
            section10Post: ", which also details the cookie policy.",
            section11Title: "11. Modification of the Terms",
            section11PPre: "",
            section11PPost: " may modify these Terms of Use at any time. The applicable version is the one published on the site at the date of consultation.",
            section12Title: "12. Applicable law",
            section12P1: "These Terms are governed by French law.",
            section12P2: "Any dispute relating to the use of the site falls within the jurisdiction of the competent French courts.",
        },
        confidentialite: {
            kicker: "Privacy Policy",
            title: "Your personal data. Our commitment.",
            lead:
                "In accordance with Regulation (EU) 2016/679 (GDPR) and the French Data Protection Act, we guarantee the confidentiality, security and respect of the rights relating to your personal data.",
            s1Title: "1. Processing purposes",
            s1P:
                "The personal data collected on this site is processed to: respond to meeting requests, transmit information at your request, ensure the follow-up of professional relationships, meet legal and tax obligations, and improve the browsing experience.",
            s2Title: "2. Data collected",
            s2P1:
                "We only collect strictly necessary data: surname, first name, position, company, email address, telephone, as well as the content of the messages you send us.",
            s2P2:
                "No sensitive data (origin, religion, opinions, health, etc.) is collected.",
            s3Title: "3. Contact forms",
            s3P:
                "The contact forms on the site transmit your requests securely to the firm. No data is transferred to third parties for commercial purposes.",
            s4Title: "4. Cookies",
            s4P:
                "The site only uses technical cookies strictly necessary for its operation. No advertising trackers, no third-party behavioural analysis tools.",
            s5Title: "5. Legal basis for processing",
            s5P:
                "Processing is based on the firm's legitimate interest in responding to the requests received, on the performance of contractual relationships, and — where applicable — on the explicit consent of the user.",
            s6Title: "6. Retention period",
            s6P:
                "Prospecting data: 3 years from the last contact. Contractual data: duration of the relationship plus legal retention periods. Accounting data: 10 years.",
            s7Title: "7. Recipients of the data",
            s7P:
                "The data is strictly reserved for authorised members of the firm. No transmission to third parties, except for technical providers bound by a contractual obligation of confidentiality (hosting, secure messaging).",
            s8Title: "8. Data security",
            s8P:
                "The data is stored on secure servers, protected by appropriate technical and organisational measures: encryption, strict access control, encrypted backups, access logging.",
            s9Title: "9. Your rights",
            s9P:
                "In accordance with the GDPR, you have the following rights over your personal data:",
            s9Items: [
                "Right of access — obtain a copy of your data",
                "Right of rectification — correct erroneous information",
                "Right to object — refuse certain processing",
                "Right to erasure — delete your data",
                "Right to restriction — restrict usage",
                "Right to portability — recover your data",
            ],
            s9PExerciseA: "You can exercise these rights at any time via the form below or by email at ",
            s9PExerciseB: ".",
            requestTypes: [
                { v: "access", l: "Right of access" },
                { v: "rectification", l: "Right of rectification" },
                { v: "opposition", l: "Right to object" },
                { v: "erasure", l: "Right to erasure" },
                { v: "limitation", l: "Right to restriction" },
                { v: "portability", l: "Right to portability" },
                { v: "other", l: "Other request" },
            ],
            formKicker: "Exercise my rights",
            formTitle: "GDPR rights exercise form.",
            formIntro:
                "We process all requests within one month. Proof of identity may be requested.",
            formFullName: "Full name",
            formEmail: "Email address",
            formType: "Type of request",
            formMessage: "Specify your request",
            formMessagePlaceholder: "Describe your request precisely…",
            formError: "An error occurred. Please try again or contact us by email.",
            formSubmit: "Send my request",
            formSubmitting: "Sending…",
            successKicker: "Request recorded",
            successTitle: "Your request has reached us.",
            successText:
                "We will respond within one month to the address provided. A confirmation email has been sent to our Data Protection Officer.",
        },
        faq: {
            kicker: "Frequently asked",
            title1: "The questions",
            title2: "we are often asked.",
        },
    },
};
